import { useState } from "react";
import { Globe, Phone, Mail, MapPin, User, Zap, Settings,
  MessageSquare, Send, ChevronDown, Star, CheckCircle,
  AlertCircle, Building2, Megaphone, ClipboardList, ArrowRight,
  Shield, Lightbulb, Wrench, BadgeCheck, ExternalLink
} from "lucide-react";
import logo from "./assets/logo.png";

// Use the same Web App URL for both; the Google Apps Script routes by formType (see google-apps-script/)
const SHEET_URLS = {
  inquiry: "https://script.google.com/macros/s/AKfycbweWmDQfXoBz7OQquTEDDLdTlYydFCr66XCHoBqSxFO6Yj-EmiKLpS7y9lI7mQBvIZ5/exec",
  feedback: "https://script.google.com/macros/s/AKfycbweWmDQfXoBz7OQquTEDDLdTlYydFCr66XCHoBqSxFO6Yj-EmiKLpS7y9lI7mQBvIZ5/exec",
};

function Logo() {
  return (
    <img src={logo} alt="Intellisolar" className="w-48" />
  );
}

// ─── FIELD ───
function Field({ label, icon: Icon, required, children, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-widest">
        {Icon && <Icon className="w-3.5 h-3.5 text-amber-500" strokeWidth={2.5} />}
        {label}
        {required && <span className="text-amber-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// ─── INPUT ───
function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400
        focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 focus:bg-white transition-all duration-200"
    />
  );
}

// ─── SELECT ───
function Select({ children, ...props }) {
  return (
    <div className="relative">
      <select
        {...props}
        className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm text-slate-800
          focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 focus:bg-white transition-all duration-200 cursor-pointer"
      >
        {children}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
    </div>
  );
}

// ─── TEXTAREA ───
function Textarea(props) {
  return (
    <textarea
      {...props}
      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400
        focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 focus:bg-white transition-all duration-200 resize-none"
    />
  );
}

// ─── STAR RATING ───
function Rating({ value, onChange }) {
  const [hov, setHov] = useState(null);
  const labels = ["Poor", "Fair", "Good", "Great", "Excellent"];
  const active = hov ?? Number(value);
  return (
    <div className="flex items-center gap-2">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button"
          onClick={() => onChange(String(n))}
          onMouseEnter={() => setHov(n)}
          onMouseLeave={() => setHov(null)}
          className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-all duration-150
            ${n <= active ? "bg-amber-400 border-amber-400 text-slate-900 shadow-sm" : "bg-slate-50 border-slate-200 text-slate-300 hover:border-amber-300"}`}
        >
          <Star className="w-4 h-4" fill={n <= active ? "currentColor" : "none"} strokeWidth={1.5} />
        </button>
      ))}
      {active > 0 && <span className="text-xs text-amber-600 font-semibold ml-1">{labels[active - 1]}</span>}
    </div>
  );
}

// ─── TOAST ───
function Toast({ type, message }) {
  if (!message) return null;
  const ok = type === "success";
  return (
    <div className={`mx-8 mb-5 flex items-start gap-3 p-4 rounded-2xl border text-sm font-medium
      ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
      {ok ? <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" /> : <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />}
      {message}
    </div>
  );
}

// ─── SECTION DIVIDER ───
function SectionDivider({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-3 my-6">
      <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-amber-600" strokeWidth={2.5} />
      </div>
      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest shrink-0">{label}</span>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

// ─── SUBMIT BTN ───
function SubmitButton({ loading, label }) {
  return (
    <button type="submit" disabled={loading}
      className="flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold tracking-wide uppercase
        bg-slate-900 text-white border-2 border-slate-900
        hover:bg-amber-400 hover:border-amber-400 hover:text-slate-900
        active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading
        ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending…</>
        : <><Send className="w-4 h-4" strokeWidth={2} />{label}</>}
    </button>
  );
}

// ─── INQUIRY FORM ───
function InquiryForm() {
  const init = {
    fullName: "", companyName: "", designation: "", phone: "", email: "", city: "",
    projectType: "", capacity: "", systemType: "", solutionCategory: "", source: "", message: ""
  };
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setToast({ type: "", message: "" });
    try {
      await fetch(SHEET_URLS.inquiry, { method: "POST", mode: "no-cors", headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ ...form, formType: "inquiry", submittedAt: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) }) });
      setToast({ type: "success", message: "Your inquiry has been submitted! Our team will reach out within 24 hours." });
      setForm(init);
    } catch { setToast({ type: "error", message: "Submission failed. Please try again or contact us directly." }); }
    setLoading(false);
  };

  return (
    <form onSubmit={submit}>
      <div className="bg-slate-900 px-8 py-6 flex items-center gap-4 border-b-2 border-amber-400">
        <div className="w-11 h-11 bg-amber-400 rounded-2xl flex items-center justify-center shrink-0">
          <ClipboardList className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="text-white font-bold text-lg tracking-tight">Solar Project Inquiry</h2>
          <p className="text-slate-400 text-sm mt-0.5">Tell us about your project — we respond within 24 hours</p>
        </div>
      </div>

      <Toast {...toast} />

      <div className="px-8 pb-8 pt-2">
        <SectionDivider icon={User} label="Personal Information" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full Name" icon={User} required>
            <Input value={form.fullName} onChange={set("fullName")} placeholder="e.g. John Doe" required />
          </Field>
          <Field label="Company Name" icon={Building2}>
            <Input value={form.companyName} onChange={set("companyName")} placeholder="e.g. ABC Industries Pvt Ltd" />
          </Field>
          <Field label="Designation" icon={BadgeCheck}>
            <Input value={form.designation} onChange={set("designation")} placeholder="e.g. Project Manager, Owner" />
          </Field>
          <Field label="Phone Number" icon={Phone} required>
            <Input value={form.phone} onChange={set("phone")} placeholder="e.g. +91 98765 43210" required />
          </Field>
          <Field label="Email Address" icon={Mail} required>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="e.g. john.doe@company.com" required />
          </Field>
          <Field label="Project Location" icon={MapPin} hint="Enter the city or site where the project will be located — not your office address">
            <Input value={form.city} onChange={set("city")} placeholder="e.g. Jaipur, Rajasthan (project site)" />
          </Field>
        </div>

        <SectionDivider icon={Zap} label="Project Details (optional)" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Project Type" icon={Building2}>
            <Select value={form.projectType} onChange={set("projectType")}>
              <option value="">Select project type</option>
              <option>Commercial</option>
              <option>Industrial</option>
              <option>Agricultural</option>
              <option>Government / Institutional</option>
              <option>PM Kusum-A</option>
              <option>PM Kusum-C</option>
              <option>PM Surya Ghar Yojana</option>
              <option>IPP Projects</option>
              <option>Sale to DISCOM Projects</option>
              <option>Agricultural Pump Monitoring System</option>
              <option>EMS Solutions</option>
              <option>Captive Solar Projects</option>
              <option>Ground-Mounted Solar Projects</option>
              <option>Other</option>
            </Select>
          </Field>
          <Field label="System Type" icon={Settings}>
            <Select value={form.systemType} onChange={set("systemType")}>
              <option value="">Select system type</option>
              <option>On-Grid</option>
              <option>Hybrid</option>
              <option>Not Sure</option>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Required Capacity" icon={Zap} hint="Typical range: 300 kW to 500 MW+">
              <Input value={form.capacity} onChange={set("capacity")} placeholder="e.g. 10 MW utility-scale" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Solution Category" icon={Wrench}>
              <Select value={form.solutionCategory} onChange={set("solutionCategory")}>
                <option value="">Select solution type</option>
                <option>Software Solutions for Renewable Sectors</option>
                <option>Customized Solutions</option>
                <option>SCADA Solutions</option>
                <option>RMS Solutions</option>
                <option>EMS Solutions</option>
                <option>CMS Solutions</option>
                <option>BESS Monitoring Solutions</option>
                <option>Wind Monitoring Solutions</option>
                <option>Hybrid Project Monitoring Solutions (Solar + Wind + BESS)</option>
                <option>Agricultural Pump Monitoring Solutions</option>
                <option>CMS Solutions for PM Kusum, Captive, and Utility-Scale Projects</option>
                <option>PPC Integration Solutions</option>
              </Select>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="How did you hear about us?" icon={Megaphone}>
              <Select value={form.source} onChange={set("source")}>
                <option value="">Select a source</option>
                <option>Facebook / Instagram</option><option>Google Search</option>
                <option>Referral / Friend</option><option>Exhibition / Event</option>
                <option>Existing Customer</option><option>Other</option>
              </Select>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Additional Message" icon={MessageSquare}>
              <Textarea value={form.message} onChange={set("message")} rows={4}
                placeholder="Site details, load requirements, or any specific questions…" />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <p className="flex items-center gap-2 text-xs text-slate-400 max-w-xs leading-relaxed">
            <Shield className="w-4 h-4 text-slate-300 shrink-0" />
            Your data is confidential and used solely to process your inquiry.
          </p>
          <SubmitButton loading={loading} label="Submit Inquiry" />
        </div>
      </div>
    </form>
  );
}

// Types that need a project name (so we can see which project the complaint is about)
const FEEDBACK_TYPES_NEED_PROJECT = ["Complaint", "After-Sales Issue", "Billing / Payment Issue"];

// ─── FEEDBACK FORM ───
function FeedbackForm() {
  const init = {
    fullName: "", phone: "", email: "", city: "", type: "", projectName: "",
    department: "", subject: "", description: "", rating: "", contactPref: ""
  };
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const showProjectName = FEEDBACK_TYPES_NEED_PROJECT.includes(form.type);
  const isComplaintOrIssue = form.type === "Complaint" || form.type === "After-Sales Issue" || form.type === "Billing / Payment Issue";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setToast({ type: "", message: "" });
    try {
      await fetch(SHEET_URLS.feedback, { method: "POST", mode: "no-cors", headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ ...form, formType: "feedback", submittedAt: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) }) });
      setToast({ type: "success", message: "Thank you for your feedback. We'll review it promptly and get back to you." });
      setForm(init);
    } catch { setToast({ type: "error", message: "Submission failed. Please try again or contact us directly." }); }
    setLoading(false);
  };

  return (
    <form onSubmit={submit}>
      <div className="bg-slate-900 px-8 py-6 flex items-center gap-4 border-b-2 border-amber-400">
        <div className="w-11 h-11 bg-amber-400 rounded-2xl flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="text-white font-bold text-lg tracking-tight">Complaint · Feedback · Suggestion</h2>
          <p className="text-slate-400 text-sm mt-0.5">We take every submission seriously — help us improve</p>
        </div>
      </div>

      <Toast {...toast} />

      <div className="px-8 pb-8 pt-2">
        <SectionDivider icon={Lightbulb} label="What would you like to share?" />
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Field label="Type of Submission" icon={BadgeCheck} required>
            <Select value={form.type} onChange={set("type")} required>
              <option value="" disabled>Select type</option>
              <option>Complaint</option><option>Feedback</option><option>Suggestion</option>
              <option>After-Sales Issue</option><option>Billing / Payment Issue</option>
            </Select>
          </Field>
        </div>

        {showProjectName && (
          <>
            <SectionDivider icon={Building2} label="Project (for complaints / issues)" />
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="col-span-2">
                <Field label="Project Name / Reference" icon={ClipboardList} hint="So we can identify which installation or order this is about">
                  <Input value={form.projectName} onChange={set("projectName")} placeholder="e.g. Commercial 500 kW - Pune, Order #12345" />
                </Field>
              </div>
            </div>
          </>
        )}

        <SectionDivider icon={User} label="Contact Details" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full Name" icon={User} required>
            <Input value={form.fullName} onChange={set("fullName")} placeholder="e.g. John Doe" required />
          </Field>
          <Field label="Phone Number" icon={Phone} required>
            <Input value={form.phone} onChange={set("phone")} placeholder="e.g. +91 98765 43210" required />
          </Field>
          <Field label="Email Address" icon={Mail}>
            <Input type="email" value={form.email} onChange={set("email")} placeholder="e.g. john.doe@email.com" />
          </Field>
          <Field label="City" icon={MapPin}>
            <Input value={form.city} onChange={set("city")} placeholder="e.g. Pune, Maharashtra" />
          </Field>
        </div>

        <SectionDivider icon={MessageSquare} label="Details" />
        <div className="grid grid-cols-2 gap-4">
          {isComplaintOrIssue && (
            <div className="col-span-2">
              <Field label="Department (if relevant)" icon={Wrench}>
                <Select value={form.department} onChange={set("department")}>
                  <option value="">Select department</option>
                  <option>Sales Team</option><option>Installation Team</option>
                  <option>After-Sales / Support</option><option>Accounts / Finance</option>
                  <option>Management</option><option>Other</option>
                </Select>
              </Field>
            </div>
          )}
          <div className="col-span-2">
            <Field label="Subject / Title" icon={ClipboardList} required>
              <Input value={form.subject} onChange={set("subject")} placeholder="e.g. Installation delay at site" required />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Description" icon={MessageSquare} required>
              <Textarea value={form.description} onChange={set("description")} rows={5} required
                placeholder="Describe what happened. Include dates, order or job numbers if you have them." />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Overall Experience (optional)" icon={Star}>
              <Rating value={form.rating} onChange={v => setForm(p => ({ ...p, rating: v }))} />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Preferred Contact Method" icon={Phone}>
              <Select value={form.contactPref} onChange={set("contactPref")}>
                <option value="">How should we reach you?</option>
                <option>Phone Call</option><option>WhatsApp</option>
                <option>Email</option><option>No response needed</option>
              </Select>
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <p className="flex items-center gap-2 text-xs text-slate-400 max-w-xs leading-relaxed">
            <Shield className="w-4 h-4 text-slate-300 shrink-0" />
            Your feedback is treated with full confidentiality.
          </p>
          <SubmitButton loading={loading} label="Submit Feedback" />
        </div>
      </div>
    </form>
  );
}

// ─── ROOT ───
export default function IntelliSolarForms() {
  const [tab, setTab] = useState("inquiry");

  return (
    <div className="min-h-screen bg-slate-100">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap'); body { font-family: 'DM Sans', sans-serif; }`}</style>

      {/* NAVBAR */}
      <nav className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <Logo />
        <a href="https://intellisolar.in" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-700 text-slate-300 text-sm font-medium
            hover:border-amber-400 hover:text-amber-400 transition-all duration-200 group">
          <Globe className="w-4 h-4" />
          Visit Website
          <ExternalLink className="w-3 h-3 opacity-60" />
        </a>
      </nav>

      {/* HERO */}
      <div className="bg-slate-900 px-6 pt-12 pb-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage:"repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",backgroundSize:"24px 24px"}} />
        <div className="relative z-10">
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
            How Can We <span className="text-amber-400">Help You?</span>
          </h1>
          <p className="text-slate-400 text-base max-w-md mx-auto leading-relaxed">
            From first-time inquiries to after-sales support — our team is here for you.
          </p>
        </div>
      </div>

      {/* TAB SWITCHER */}
      <div className="flex justify-center -mt-6 px-6 mb-8 relative z-10">
        <div className="inline-flex bg-white rounded-2xl shadow-xl border border-slate-200 p-1.5 gap-1">
          {[
            { key:"inquiry", label:"Inquiry Form", icon:ClipboardList },
            { key:"feedback", label:"Complaint / Feedback", icon:MessageSquare },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200
                ${tab === key ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
              <Icon className={`w-4 h-4 ${tab === key ? "text-amber-400" : ""}`} strokeWidth={2} />
              {label}
              {tab === key && <ArrowRight className="w-3.5 h-3.5 text-amber-400" />}
            </button>
          ))}
        </div>
      </div>

      {/* FORM */}
      <main className="px-6 pb-16 max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl shadow-lg border border-slate-200 overflow-hidden">
          {tab === "inquiry" ? <InquiryForm /> : <FeedbackForm />}
        </div>

        {/* Trust badges */}
        <div className="flex items-center justify-center gap-8 mt-8 flex-wrap">
          {[
            { icon: Shield, text: "Secure & Confidential" },
            { icon: Zap, text: "24-hr Response Time" },
            { icon: BadgeCheck, text: "Certified Engineers" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
              <Icon className="w-3.5 h-3.5 text-amber-400" strokeWidth={2.5} />
              {text}
            </div>
          ))}
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 py-5 text-center text-xs text-slate-400 tracking-wide">
        © 2026 IntelliSolar · Smart Energy Solutions · All Rights Reserved
      </footer>
    </div>
  );
}